import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TimerIcon from '@mui/icons-material/Timer';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AKTIVRETTUNG_DESCRIPTION,
  AKTIVRETTUNG_GRADE_RANGES,
  AKTIVRETTUNG_MATERIAL,
  AKTIVRETTUNG_SECTION_DEFINITIONS,
  AKTIVRETTUNG_TIME_POINT_RULES,
  AKTIVRETTUNG_TIMEPOINT_SECTION_IDS,
  buildEmptyFitnessCoordinationResult,
  buildEmptyAktivrettungResult,
  buildEmptyEinzelueberpruefungResult,
  buildEmptyGruppenpruefungResult,
  buildInitialPracticalSheetsState,
  buildPracticalSheetsStateFromSavedSheets,
  buildPracticalSheetsStorageKey,
  EINZELUEBERPRUEFUNG_DESCRIPTION,
  EINZELUEBERPRUEFUNG_GRADE_RANGES,
  EINZELUEBERPRUEFUNG_MATERIAL,
  EINZELUEBERPRUEFUNG_SECTION_DEFINITIONS,
  EINZELUEBERPRUEFUNG_TIME_POINT_RULES,
  FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER,
  FITNESS_COORDINATION_TIME_SOURCE_MEASURED,
  FITNESS_COORDINATION_EQUIPMENT,
  FITNESS_COORDINATION_NOTES,
  FITNESS_COORDINATION_SEQUENCE,
  formatDuration,
  getFitnessCoordinationEffectiveTimeSource,
  getFitnessCoordinationDisplayMs,
  getEffectiveFitnessCoordinationTotalMs,
  getAktivrettungGrade,
  getAktivrettungGradeLabel,
  getAktivrettungOverallPoints,
  getAktivrettungSectionSum,
  getAktivrettungTimePoints,
  getEinzelueberpruefungGrade,
  getEinzelueberpruefungGradeLabel,
  getEinzelueberpruefungOverallPoints,
  getEinzelueberpruefungSectionSum,
  getEinzelueberpruefungTimePoints,
  getFitnessCoordinationGrade,
  getFitnessCoordinationGradeLabel,
  isGroupPracticalStation,
  isEinzelueberpruefungFailed,
  isFitnessCoordinationFailed,
  isFitnessCoordinationControlWithinTolerance,
  isAktivrettungFailed,
  parseMmSsToMs,
  roundToSecondMs,
  SRHT_EXAM_PRACTICAL_STATIONS,
  SRHT_EXAM_STATION_AKTIVRETTUNG,
  SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG,
  SRHT_EXAM_STATION_FALLSCHUTZ,
  SRHT_EXAM_STATION_FITNESS_COORDINATION,
  SRHT_EXAM_STATION_GRUPPENPRUEFUNG,
} from './srhtExamPracticalSheets';
import SrhtExamGroupPracticalPanel from './SrhtExamGroupPracticalPanel';
import { sanitizeSrhtTimerStates } from '../../utils/persistentTimerState';

function readStoredJson(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
}

function writeStoredJson(storageKey, value) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore local storage write failures.
  }
}

function buildTimerStateMap() {
  return {
    lane_1: { running: false, startReference: null },
    lane_2: { running: false, startReference: null },
    lane_3: { running: false, startReference: null },
  };
}

function buildCandidateTimerKey(candidateId) {
  const normalizedCandidateId = Number(candidateId);
  if (!Number.isInteger(normalizedCandidateId) || normalizedCandidateId <= 0) {
    return '';
  }
  return `candidate_${normalizedCandidateId}`;
}

function getSelectableUserId(user) {
  return Number(user?.userId || user?.id || 0);
}

function getSelectableUserName(user) {
  return user?.fullName || '-';
}

function getGradeChipColor(grade, failed = false) {
  if (failed) return 'error';
  if (grade === 1 || grade === 2) return 'success';
  if (grade === 3 || grade === 4) return 'warning';
  if (grade === 5 || grade === 6) return 'error';
  return 'default';
}

function formatAktivrettungGradeRangeLabel(range = {}) {
  return `${range.label}: ${range.min}\u2013${range.max}`;
}

function getReadableDisabledFieldSx() {
  return {
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: (theme) => theme.palette.text.primary,
    },
    '& .MuiSelect-select.Mui-disabled': {
      WebkitTextFillColor: (theme) => theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: (theme) => theme.palette.text.secondary,
    },
  };
}

function getValueAtPath(source, path) {
  if (!source || !path) {
    return undefined;
  }

  return String(path)
    .split('.')
    .reduce((current, key) => (current == null ? undefined : current[key]), source);
}

function setValueAtPath(source, path, value) {
  const keys = String(path || '').split('.').filter(Boolean);
  if (keys.length === 0) {
    return source;
  }

  const cloneRoot = Array.isArray(source) ? [...source] : { ...(source || {}) };
  let current = cloneRoot;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
      return;
    }

    const nextValue = current[key];
    current[key] = Array.isArray(nextValue) ? [...nextValue] : { ...(nextValue || {}) };
    current = current[key];
  });

  return cloneRoot;
}

function getControlStatusChipProps(result = {}) {
  if (!Number(result.totalMs || 0)) {
    return { label: 'Kontrolle offen', color: 'default' };
  }

  if (!Number(result.controllerReferenceMs || 0)) {
    return { label: 'Kontrollzeit fehlt', color: 'error' };
  }

  return getFitnessCoordinationEffectiveTimeSource(result) === FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER
    ? { label: 'Kontrollzeit', color: 'info' }
    : { label: 'App Zeit', color: 'success' };
}

function getSimpleSheetStatusChipProps(result = {}) {
  return String(result.status || '').trim() === 'finalized'
    ? { label: 'Finalisiert', color: 'success' }
    : { label: 'Offen', color: 'default' };
}

function getScoredTimedStatusChipProps(result = {}, isRunning = false) {
  if (String(result.status || '').trim() === 'finalized') {
    return { label: 'Finalisiert', color: 'success' };
  }

  if (isRunning) {
    return { label: 'Läuft', color: 'warning' };
  }

  if (Number(result.totalMs || 0) > 0) {
    return { label: 'Zeit erfasst', color: 'success' };
  }

  if (
    result.examinerUserId
    || result.controllerUserId
    || result.remarks
    || Object.values(result.sectionScores || {}).some((value) => value !== '' && value !== null && typeof value !== 'undefined')
  ) {
    return { label: 'In Bearbeitung', color: 'primary' };
  }

  return { label: 'Noch offen', color: 'default' };
}

export default function SrhtExamPracticalSheetsPanel({
  examId,
  examLabel,
  candidates = [],
  examiners = [],
  timeControllerOptions = [],
  sheetRecords = {},
  canEdit,
  canEditByPermission = true,
  isExamLocked = false,
  isOffline = false,
  actionBusy = '',
  onSyncTimerSession,
  onSave,
  onFinalize,
  onUnlock,
}) {
  const initialPracticalSheetsState = useMemo(() => buildInitialPracticalSheetsState(), []);
  const hydratedExamIdRef = useRef('');
  const [sheetState, setSheetState] = useState(() => buildInitialPracticalSheetsState());
  const sheetStateRef = useRef(initialPracticalSheetsState);
  const [timerStates, setTimerStates] = useState(() => buildTimerStateMap());
  const [timerTick, setTimerTick] = useState(Date.now());
  const [unlockDialog, setUnlockDialog] = useState({ open: false, reason: '' });
  const [manualTimeDialog, setManualTimeDialog] = useState({
    open: false,
    candidateId: '',
    field: '',
    label: '',
    value: '00:00',
    error: '',
  });
  const [finalizeMissingTimesDialogOpen, setFinalizeMissingTimesDialogOpen] = useState(false);
  const [examinerDialog, setExaminerDialog] = useState({
    open: false,
    candidateId: '',
    examinerUserId: '',
    controllerUserId: '',
  });
  const [safetyIncidentDialog, setSafetyIncidentDialog] = useState({ open: false, description: '' });
  const [sheetValidationNotice, setSheetValidationNotice] = useState(null);

  const defaultFitnessStation = initialPracticalSheetsState.stations[SRHT_EXAM_STATION_FITNESS_COORDINATION];
  const defaultAktivrettungStation = initialPracticalSheetsState.stations[SRHT_EXAM_STATION_AKTIVRETTUNG];
  const defaultFallschutzStation = initialPracticalSheetsState.stations[SRHT_EXAM_STATION_FALLSCHUTZ];
  const defaultGruppenpruefungStation = initialPracticalSheetsState.stations[SRHT_EXAM_STATION_GRUPPENPRUEFUNG];
  const defaultEinzelueberpruefungStation = initialPracticalSheetsState.stations[SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG];

  const getDefaultStationState = useCallback((stationId) => {
    if (stationId === SRHT_EXAM_STATION_AKTIVRETTUNG) {
      return defaultAktivrettungStation;
    }

    if (stationId === SRHT_EXAM_STATION_FALLSCHUTZ) {
      return defaultFallschutzStation;
    }

    if (stationId === SRHT_EXAM_STATION_GRUPPENPRUEFUNG) {
      return defaultGruppenpruefungStation;
    }

    if (stationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG) {
      return defaultEinzelueberpruefungStation;
    }

    return defaultFitnessStation;
  }, [defaultAktivrettungStation, defaultEinzelueberpruefungStation, defaultFallschutzStation, defaultFitnessStation, defaultGruppenpruefungStation]);

  useEffect(() => {
    sheetStateRef.current = sheetState;
  }, [sheetState]);

  useEffect(() => {
    if (!examId) {
      hydratedExamIdRef.current = '';
      setSheetState(buildInitialPracticalSheetsState());
      setTimerStates(buildTimerStateMap());
      setTimerTick(Date.now());
      return;
    }

    const storedValue = readStoredJson(buildPracticalSheetsStorageKey(examId));
    const isSameExam = String(hydratedExamIdRef.current || '') === String(examId || '');
    const nextSheetState = buildPracticalSheetsStateFromSavedSheets(
      sheetRecords,
      isSameExam ? sheetStateRef.current : (storedValue?.stations ? storedValue : null),
    );
    const nextActiveStationId = nextSheetState.activeStationId || SRHT_EXAM_STATION_FITNESS_COORDINATION;
    hydratedExamIdRef.current = String(examId || '');
    setSheetState(nextSheetState);
    setTimerStates(sanitizeSrhtTimerStates(buildTimerStateMap(), sheetRecords?.[nextActiveStationId]?.timerSession));
    setTimerTick(Date.now());
  }, [examId, sheetRecords]);

  useEffect(() => {
    if (!examId) {
      return;
    }
    writeStoredJson(buildPracticalSheetsStorageKey(examId), {
      ...sheetState,
    });
  }, [examId, sheetState]);

  useEffect(() => {
    const hasRunningTimer = Object.values(timerStates).some((state) => state.running && state.startReference != null);
    if (!hasRunningTimer) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimerTick(Date.now());
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [timerStates]);

  const activeStationId = sheetState.activeStationId || SRHT_EXAM_STATION_FITNESS_COORDINATION;
  const activeStation = sheetState.stations?.[activeStationId] || getDefaultStationState(activeStationId);
  const activeSheetRecord = sheetRecords?.[activeStationId] || null;
  const activeSheetStatus = activeSheetRecord?.status || 'not_saved';
  const resultsByCandidateId = activeStation.resultsByCandidateId || {};
  const selectedCandidateId = String(activeStation.selectedCandidateId || candidates[0]?.id || '');

  useEffect(() => {
    setTimerStates(sanitizeSrhtTimerStates(buildTimerStateMap(), activeSheetRecord?.timerSession));
    setTimerTick(Date.now());
  }, [activeSheetRecord?.timerSession, activeStationId]);

  useEffect(() => {
    if (candidates.length === 0) {
      return;
    }

    const hasSelectedCandidate = candidates.some((candidate) => String(candidate.id) === selectedCandidateId);
    if (hasSelectedCandidate) {
      return;
    }

    setSheetState((current) => ({
      ...current,
      stations: {
        ...current.stations,
        [activeStationId]: {
          ...(current.stations?.[activeStationId] || getDefaultStationState(activeStationId)),
          selectedCandidateId: String(candidates[0].id),
        },
      },
    }));
  }, [activeStationId, candidates, getDefaultStationState, selectedCandidateId]);

  useEffect(() => {
    setFinalizeMissingTimesDialogOpen(false);
  }, [activeStationId, selectedCandidateId]);

  useEffect(() => {
    setExaminerDialog({ open: false, candidateId: '', examinerUserId: '', controllerUserId: '' });
    setSafetyIncidentDialog({ open: false, description: '' });
  }, [activeStationId, selectedCandidateId]);

  const examinerById = useMemo(
    () => new Map(examiners.map((examiner) => [String(getSelectableUserId(examiner)), examiner])),
    [examiners],
  );
  const controllerById = useMemo(
    () => new Map(timeControllerOptions.map((option) => [String(getSelectableUserId(option)), option])),
    [timeControllerOptions],
  );

  const updateActiveStation = (updater) => {
    setSheetState((current) => {
      const currentStation = current.stations?.[activeStationId] || getDefaultStationState(activeStationId);
      const nextStation = typeof updater === 'function' ? updater(currentStation) : updater;
      return {
        ...current,
        stations: {
          ...current.stations,
          [activeStationId]: nextStation,
        },
      };
    });
  };

  const replaceActiveStation = useCallback((nextStation) => {
    setSheetState((current) => ({
      ...current,
      stations: {
        ...current.stations,
        [activeStationId]: nextStation,
      },
    }));
  }, [activeStationId]);

  const handleSelectStation = (stationId) => {
    setSheetState((current) => ({ ...current, activeStationId: stationId }));
  };

  const handleSelectCandidate = (candidateId) => {
    setSheetValidationNotice(null);
    updateActiveStation((station) => ({ ...station, selectedCandidateId: String(candidateId) }));
  };

  const buildEmptyResultForStation = useCallback((stationId = activeStationId) => (
    stationId === SRHT_EXAM_STATION_AKTIVRETTUNG
      ? buildEmptyAktivrettungResult()
      : isGroupPracticalStation(stationId)
        ? buildEmptyGruppenpruefungResult()
      : stationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG
        ? buildEmptyEinzelueberpruefungResult()
        : buildEmptyFitnessCoordinationResult()
  ), [activeStationId]);

  const getLaneForCandidate = (candidateId) => {
    const result = {
      ...buildEmptyResultForStation(activeStationId),
      ...(resultsByCandidateId[String(candidateId)] || {}),
    };
    return {
      id: String(result.legacyLaneId || result.laneId || buildCandidateTimerKey(candidateId) || ''),
      candidateUserId: Number(candidateId) || '',
      examinerUserId: result.examinerUserId || '',
      controllerUserId: result.controllerUserId || '',
    };
  };
  const getTimerKeyForCandidate = useCallback((candidateId, lane = null) => {
    const candidateTimerKey = buildCandidateTimerKey(candidateId);
    if (candidateTimerKey && timerStates[candidateTimerKey]) {
      return candidateTimerKey;
    }
    if (lane?.id && timerStates[lane.id]) {
      return lane.id;
    }
    if (candidateTimerKey) {
      return candidateTimerKey;
    }
    return lane?.id || '';
  }, [timerStates]);

  const getResultForCandidate = (candidateId) => ({
    ...buildEmptyResultForStation(activeStationId),
    ...(resultsByCandidateId[String(candidateId)] || {}),
  });

  const activeCandidate = candidates.find((candidate) => String(candidate.id) === selectedCandidateId) || null;
  const activeLane = activeCandidate ? getLaneForCandidate(activeCandidate.id) : null;
  const activeTimerKey = activeCandidate ? getTimerKeyForCandidate(activeCandidate.id, activeLane) : '';
  const activeResult = activeCandidate ? getResultForCandidate(activeCandidate.id) : buildEmptyResultForStation(activeStationId);
  const missingAktivrettungTimeSections = useMemo(() => {
    if (activeStationId !== SRHT_EXAM_STATION_AKTIVRETTUNG) {
      return [];
    }

    return AKTIVRETTUNG_SECTION_DEFINITIONS
      .filter((section) => section.capturesTimestamp)
      .filter((section) => !Number(activeResult.sectionTimes?.[section.id] || 0));
  }, [activeResult.sectionTimes, activeStationId]);
  const missingFitnessIntermediateTimes = useMemo(() => {
    if (activeStationId !== SRHT_EXAM_STATION_FITNESS_COORDINATION) {
      return [];
    }

    const missingTimes = [];
    if (!Number(activeResult.firstIntermediateMs || 0)) {
      missingTimes.push('1. Zwischenzeit nach 1. Anschlag');
    }
    if (!Number(activeResult.secondIntermediateMs || 0)) {
      missingTimes.push('2. Zwischenzeit nach 2. Anschlag');
    }
    return missingTimes;
  }, [activeResult.firstIntermediateMs, activeResult.secondIntermediateMs, activeStationId]);
  const missingFinalizeTimeLabels = useMemo(() => {
    if (activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG) {
      return missingAktivrettungTimeSections.map((section) => `Abschnitt ${section.number}`);
    }

    if (activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION) {
      return missingFitnessIntermediateTimes;
    }

    return [];
  }, [activeStationId, missingAktivrettungTimeSections, missingFitnessIntermediateTimes]);
  const missingFinalizeTimesDialogConfig = useMemo(() => {
    if (activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION) {
      return {
        title: 'Zwischenzeiten unvollständig',
        message: 'Für die Fitness- & Koordinationsprüfung fehlen noch Zwischenzeiten. Diese Angaben sind optional und Sie können den Prüfbogen trotzdem finalisieren.',
      };
    }

    return {
      title: 'Zwischenzeiten unvollständig',
      message: 'Für die Aktivrettung fehlen noch Zwischenzeiten in einzelnen Abschnitten. Diese Angaben sind optional und Sie können den Prüfbogen trotzdem finalisieren.',
    };
  }, [activeStationId]);
  const isActiveCandidateFinalized = String(activeResult.status || '').trim() === 'finalized';
  const isActiveSheetFinalized = activeSheetStatus === 'finalized';
  const blockedByPermission = !canEditByPermission;
  const blockedByExamLock = isExamLocked;
  const blockedByOffline = isOffline;
  const blockedByFinalizedCandidate = isActiveCandidateFinalized;
  const canEditFitnessSheet = !blockedByPermission && !blockedByExamLock && !blockedByOffline && !blockedByFinalizedCandidate;

  const practicalEditBlockMessage = useMemo(() => {
    if (blockedByPermission) {
      return {
        severity: 'warning',
        message: 'Für praktische Prüfungsbögen fehlen die nötigen Berechtigungen. Lehrhöhenretter und höher dürfen bearbeiten.',
      };
    }

    if (blockedByExamLock) {
      return {
        severity: 'info',
        message: 'Diese Prüfung ist geschlossen und schreibgeschützt. Für Änderungen muss sie zuerst entsperrt werden.',
      };
    }

    if (blockedByOffline) {
      return {
        severity: 'info',
        message: 'Der Offlinebetrieb für Prüfung SRHT ist deaktiviert. Für Änderungen ist eine Serververbindung erforderlich.',
      };
    }

    return null;
  }, [blockedByExamLock, blockedByOffline, blockedByPermission]);

  const buildStationWithUpdatedCandidateResult = useCallback((station, candidateId, updater, stationId = activeStationId) => {
    const currentResult = {
      ...buildEmptyResultForStation(stationId),
      ...(station.resultsByCandidateId?.[String(candidateId)] || {}),
    };
    const nextResult = typeof updater === 'function' ? updater(currentResult) : { ...currentResult, ...updater };

    return {
      ...station,
      resultsByCandidateId: {
        ...(station.resultsByCandidateId || {}),
        [String(candidateId)]: {
          ...nextResult,
          updatedAt: new Date().toISOString(),
        },
      },
    };
  }, [activeStationId, buildEmptyResultForStation]);

  const serializeRunningTimerSession = useCallback((nextTimerStates) => {
    const normalizedStates = nextTimerStates && typeof nextTimerStates === 'object' ? nextTimerStates : {};
    const runningEntries = Object.entries(normalizedStates)
      .filter(([, state]) => state?.running && Number.isFinite(Number(state.startReference)) && Number(state.startReference) > 0)
      .map(([timerKey, state]) => [timerKey, { running: true, startReference: Math.round(Number(state.startReference)) }]);

    return runningEntries.length > 0 ? Object.fromEntries(runningEntries) : null;
  }, []);

  const sanitizeStationForPersistence = useCallback((station) => {
    const nextStation = {
      ...(station || {}),
    };
    delete nextStation.lanes;

    nextStation.resultsByCandidateId = Object.fromEntries(
      Object.entries(nextStation.resultsByCandidateId || {}).map(([candidateId, result]) => {
        const nextResult = {
          ...(result || {}),
        };
        delete nextResult.laneId;
        delete nextResult.legacyLaneId;
        return [String(candidateId), nextResult];
      }),
    );

    return nextStation;
  }, []);

  const commitTimerMutation = useCallback(async (nextStation, nextTimerStates) => {
    try {
      if (onSyncTimerSession) {
        await onSyncTimerSession(activeStationId, sanitizeStationForPersistence(nextStation), serializeRunningTimerSession(nextTimerStates));
      }
    } catch {
      return false;
    }

    replaceActiveStation(nextStation);
    setTimerStates(nextTimerStates);
    setTimerTick(Date.now());
    return true;
  }, [activeStationId, onSyncTimerSession, replaceActiveStation, sanitizeStationForPersistence, serializeRunningTimerSession]);

  const persistStationSilently = useCallback(async (nextStation) => {
    const previousStation = activeStation;
    replaceActiveStation(nextStation);

    if (!onSave) {
      return true;
    }

    try {
      await onSave(activeStationId, sanitizeStationForPersistence(nextStation), { silent: true });
      return true;
    } catch {
      replaceActiveStation(previousStation);
      return false;
    }
  }, [activeStation, activeStationId, onSave, replaceActiveStation, sanitizeStationForPersistence]);

  const persistCandidateResultSilently = useCallback(async (candidateId, updater) => {
    if (!candidateId) {
      return false;
    }

    const nextStation = buildStationWithUpdatedCandidateResult(activeStation, candidateId, updater);
    return persistStationSilently(nextStation);
  }, [activeStation, buildStationWithUpdatedCandidateResult, persistStationSilently]);

  const getRunningElapsedMs = (laneId, fallbackMs = 0) => {
    const timerState = timerStates[laneId];
    if (!timerState?.running || timerState.startReference == null) {
      return Number(fallbackMs || 0);
    }
    return Math.max(0, timerTick - timerState.startReference);
  };

  const updateCandidateResult = (candidateId, updater) => {
    if (!candidateId) {
      return;
    }

    updateActiveStation((station) => {
      const currentResult = {
        ...buildEmptyResultForStation(activeStationId),
        ...(station.resultsByCandidateId?.[String(candidateId)] || {}),
      };
      const nextResult = typeof updater === 'function' ? updater(currentResult) : { ...currentResult, ...updater };

      return {
        ...station,
        resultsByCandidateId: {
          ...(station.resultsByCandidateId || {}),
          [String(candidateId)]: {
            ...nextResult,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    });
  };

  const getAvailableExaminers = (candidateId) => {
    void candidateId;
    return examiners;
  };

  const getAvailableControllers = (candidateId, examinerUserId) => {
    void candidateId;
    return timeControllerOptions.filter((option) => (
      String(getSelectableUserId(option)) !== String(examinerUserId || '')
    ));
  };

  const handleOpenExaminerDialog = (candidateId) => {
    if (!candidateId) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    setExaminerDialog({
      open: true,
      candidateId: String(candidateId),
      examinerUserId: String(result.examinerUserId || ''),
      controllerUserId: String(result.controllerUserId || ''),
    });
  };

  const handleCloseExaminerDialog = () => {
    setExaminerDialog({ open: false, candidateId: '', examinerUserId: '', controllerUserId: '' });
  };

  const handleSaveExaminerDialog = async () => {
    if (!examinerDialog.candidateId) {
      return;
    }

    const normalizedExaminerUserId = examinerDialog.examinerUserId ? Number(examinerDialog.examinerUserId) : '';
    const normalizedControllerUserId = examinerDialog.controllerUserId ? Number(examinerDialog.controllerUserId) : '';

    const saved = await persistCandidateResultSilently(examinerDialog.candidateId, (current) => ({
      ...current,
      examinerUserId: normalizedExaminerUserId,
      controllerUserId: String(normalizedExaminerUserId || '') === String(normalizedControllerUserId || '') ? '' : normalizedControllerUserId,
      ...(activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION ? {
        controllerReferenceMs: 0,
        effectiveTimeSource: FITNESS_COORDINATION_TIME_SOURCE_MEASURED,
      } : {}),
    }));

    if (saved) {
      handleCloseExaminerDialog();
    }
  };

  const handleSelectEffectiveTimeSource = (candidateId, nextSource) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    if (!Number(result.totalMs || 0) || !Number(result.controllerReferenceMs || 0) || !isFitnessCoordinationControlWithinTolerance(result)) {
      return;
    }

    updateCandidateResult(candidateId, {
      effectiveTimeSource: nextSource === FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER
        ? FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER
        : FITNESS_COORDINATION_TIME_SOURCE_MEASURED,
    });
  };

  const handleStartTimer = async (candidateId) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    const lane = getLaneForCandidate(candidateId);
    const nextStation = activeStation;

    if (!lane?.id || !result.examinerUserId || !result.controllerUserId || Number(result.totalMs || 0) > 0) {
      return;
    }

    const baseTimeMs = Math.max(Number(result.firstIntermediateMs || 0), Number(result.secondIntermediateMs || 0), 0);
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const nextTimerStates = {
      ...timerStates,
      [timerKey]: {
        running: true,
        startReference: Date.now() - baseTimeMs,
      },
    };

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const handleCaptureTime = async (candidateId, field, stopAfterCapture = false) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const lane = getLaneForCandidate(candidateId);
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    if (!timerKey) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    const capturedMs = roundToSecondMs(getRunningElapsedMs(timerKey, result[field] || 0));
    const nextStation = buildStationWithUpdatedCandidateResult(activeStation, candidateId, {
      [field]: capturedMs,
      examinerUserId: lane.examinerUserId || result.examinerUserId || '',
      controllerUserId: lane.controllerUserId || result.controllerUserId || '',
      controllerReferenceMs: field === 'totalMs' ? 0 : result.controllerReferenceMs,
      effectiveTimeSource: FITNESS_COORDINATION_TIME_SOURCE_MEASURED,
    });

    const nextTimerStates = stopAfterCapture
      ? {
        ...timerStates,
        [timerKey]: { running: false, startReference: null },
      }
      : timerStates;

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const handleResetCandidateTimes = async (candidateId) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const lane = getLaneForCandidate(candidateId);
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const nextStation = buildStationWithUpdatedCandidateResult(activeStation, candidateId, (current) => ({
      ...current,
      firstIntermediateMs: 0,
      secondIntermediateMs: 0,
      totalMs: 0,
      controllerReferenceMs: 0,
      effectiveTimeSource: FITNESS_COORDINATION_TIME_SOURCE_MEASURED,
    }));

    const nextTimerStates = timerKey
      ? {
        ...timerStates,
        [timerKey]: { running: false, startReference: null },
      }
      : timerStates;

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const openManualTimeDialog = (candidateId, field, label) => {
    const result = getResultForCandidate(candidateId);
    setManualTimeDialog({
      open: true,
      candidateId: String(candidateId),
      field,
      label,
      value: formatDuration(Number(getValueAtPath(result, field) || result[field] || 0)),
      error: '',
    });
  };

  const closeManualTimeDialog = () => {
    setManualTimeDialog({
      open: false,
      candidateId: '',
      field: '',
      label: '',
      value: '00:00',
      error: '',
    });
  };

  const confirmManualTimeDialog = async () => {
    const parsedMs = parseMmSsToMs(manualTimeDialog.value);
    if (parsedMs == null) {
      setManualTimeDialog((current) => ({ ...current, error: 'Bitte eine Zeit im Format MM:SS eingeben.' }));
      return;
    }

    const lane = getLaneForCandidate(manualTimeDialog.candidateId);
    const timerKey = getTimerKeyForCandidate(manualTimeDialog.candidateId, lane);
    let nextStation = activeStation;
    let nextTimerStates = timerStates;

    if (activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && String(manualTimeDialog.field).includes('.')) {
      nextStation = buildStationWithUpdatedCandidateResult(activeStation, manualTimeDialog.candidateId, (current) => setValueAtPath(current, manualTimeDialog.field, parsedMs));
    } else {
      const nextPatch = { [manualTimeDialog.field]: parsedMs };
      if (manualTimeDialog.field === 'totalMs') {
        nextPatch.controllerReferenceMs = 0;
        nextPatch.effectiveTimeSource = FITNESS_COORDINATION_TIME_SOURCE_MEASURED;
      }
      if (manualTimeDialog.field === 'controllerReferenceMs') {
        nextPatch.effectiveTimeSource = FITNESS_COORDINATION_TIME_SOURCE_MEASURED;
      }
      nextStation = buildStationWithUpdatedCandidateResult(activeStation, manualTimeDialog.candidateId, nextPatch);
    }

    if (manualTimeDialog.field === 'totalMs' || manualTimeDialog.field === 'sectionTimes.totalMs') {
      if (timerKey) {
        nextTimerStates = {
          ...timerStates,
          [timerKey]: { running: false, startReference: null },
        };
      }
    }

    await commitTimerMutation(nextStation, nextTimerStates);

    closeManualTimeDialog();
  };

  const renderCheckpointCard = (candidateId, lane, result, field, label, actionLabel, stopAfterCapture = false) => {
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const timerState = timerKey ? timerStates[timerKey] : null;
    const capturedValue = Number(result[field] || 0);
    const currentValue = timerState?.running && !capturedValue
      ? getRunningElapsedMs(timerKey, capturedValue)
      : capturedValue;
    const captureDisabled = !canEditFitnessSheet
      || !lane?.id
      || !lane.examinerUserId
      || !lane.controllerUserId
      || !timerState?.running
      || (field === 'secondIntermediateMs' && !Number(result.firstIntermediateMs || 0));

    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{label}</Typography>
          <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {formatDuration(currentValue)}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button size="small" variant="contained" onClick={() => handleCaptureTime(candidateId, field, stopAfterCapture)} disabled={captureDisabled}>
              {actionLabel}
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleResetFitnessIntermediateTimeField(candidateId, field)} disabled={!canEditFitnessSheet}>
              Zurücksetzen
            </Button>
            <Button size="small" variant="outlined" onClick={() => openManualTimeDialog(candidateId, field, label)} disabled={!canEditFitnessSheet}>
              Manuell
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const handleStartAktivrettungTimer = async (candidateId) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    const lane = getLaneForCandidate(candidateId);
    const nextStation = activeStation;

    if (!lane?.id || !result.examinerUserId || !result.controllerUserId || Number(result.totalMs || 0) > 0) {
      return;
    }

    const baseTimeMs = Math.max(
      Number(result.totalMs || 0),
      ...AKTIVRETTUNG_TIMEPOINT_SECTION_IDS.map((sectionId) => Number(result.sectionTimes?.[sectionId] || 0)),
      0,
    );

    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const nextTimerStates = {
      ...timerStates,
      [timerKey]: {
        running: true,
        startReference: Date.now() - baseTimeMs,
      },
    };

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const handleCaptureAktivrettungTime = async (candidateId, fieldPath, stopAfterCapture = false) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const lane = getLaneForCandidate(candidateId);
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    if (!timerKey) {
      return;
    }

    const result = getResultForCandidate(candidateId);
    const fallbackValue = Number(getValueAtPath(result, fieldPath) || 0);
    const capturedMs = roundToSecondMs(getRunningElapsedMs(timerKey, fallbackValue));

    const nextStation = buildStationWithUpdatedCandidateResult(activeStation, candidateId, (current) => {
      let nextResult = setValueAtPath(current, fieldPath, capturedMs);
      nextResult = {
        ...nextResult,
        examinerUserId: lane.examinerUserId || current.examinerUserId || '',
        controllerUserId: lane.controllerUserId || current.controllerUserId || '',
      };
      return nextResult;
    });

    const nextTimerStates = stopAfterCapture
      ? {
        ...timerStates,
        [timerKey]: { running: false, startReference: null },
      }
      : timerStates;

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const handleResetAktivrettungTimeField = async (candidateId, fieldPath) => {
    if (!canEditFitnessSheet) {
      return;
    }

    await persistCandidateResultSilently(candidateId, (current) => setValueAtPath(current, fieldPath, 0));
  };

  const handleResetFitnessIntermediateTimeField = async (candidateId, field) => {
    if (!canEditFitnessSheet) {
      return;
    }

    await persistCandidateResultSilently(candidateId, {
      [field]: 0,
    });
  };

  const handleResetAktivrettungTimes = async (candidateId) => {
    if (!canEditFitnessSheet) {
      return;
    }

    const lane = getLaneForCandidate(candidateId);
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const nextStation = buildStationWithUpdatedCandidateResult(activeStation, candidateId, (current) => ({
      ...current,
      totalMs: 0,
      sectionTimes: Object.fromEntries(AKTIVRETTUNG_TIMEPOINT_SECTION_IDS.map((sectionId) => [sectionId, 0])),
    }));

    const nextTimerStates = timerKey
      ? {
        ...timerStates,
        [timerKey]: { running: false, startReference: null },
      }
      : timerStates;

    await commitTimerMutation(nextStation, nextTimerStates);
  };

  const handleChangeAktivrettungSectionScore = async (candidateId, sectionId, nextPoints) => {
    await persistCandidateResultSilently(candidateId, (current) => ({
      ...current,
      sectionScores: {
        ...(current.sectionScores || {}),
        [sectionId]: current.sectionScores?.[sectionId] === nextPoints ? '' : nextPoints,
      },
    }));
  };

  const handleOpenSafetyIncidentDialog = () => {
    setSafetyIncidentDialog({
      open: true,
      description: activeResult.safetyIncident?.description || '',
    });
  };

  const handleCloseSafetyIncidentDialog = () => {
    setSafetyIncidentDialog({ open: false, description: '' });
  };

  const handleSaveSafetyIncidentNo = async () => {
    if (!activeCandidate?.id) {
      return;
    }

    await persistCandidateResultSilently(activeCandidate.id, (current) => ({
      ...current,
      safetyIncident: {
        ...(current.safetyIncident || {}),
        answered: true,
        hasIncident: false,
        description: '',
      },
    }));
  };

  const handleSaveSafetyIncidentYes = async () => {
    if (!activeCandidate?.id) {
      return;
    }

    const description = String(safetyIncidentDialog.description || '').trim();
    if (!description) {
      setSheetValidationNotice({
        severity: 'warning',
        message: 'Bitte eine Beschreibung zum sicherheitsrelevanten Fehler eingeben.',
      });
      return;
    }

    const saved = await persistCandidateResultSilently(activeCandidate.id, (current) => ({
      ...current,
      safetyIncident: {
        ...(current.safetyIncident || {}),
        answered: true,
        hasIncident: true,
        description,
      },
    }));

    if (saved) {
      handleCloseSafetyIncidentDialog();
      setSheetValidationNotice(null);
    }
  };

  const renderExaminerAssignmentCard = (candidateId, options = {}) => {
    const title = options.title || 'Prüfer';
    const examinerLabel = options.examinerLabel || 'Prüfer';
    const controllerLabel = options.controllerLabel || 'Kontrolleur';
    const helperText = options.helperText || '';

    return (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
            <Button variant="outlined" size="small" onClick={() => handleOpenExaminerDialog(candidateId)} disabled={!canEditFitnessSheet || activeTimerRunning}>
              Bearbeiten
            </Button>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {examinerLabel}: {getSelectableUserName(examinerById.get(String(activeResult.examinerUserId || '')))}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {controllerLabel}: {getSelectableUserName(controllerById.get(String(activeResult.controllerUserId || '')))}
            </Typography>
            {helperText ? <Typography variant="caption" color="text.secondary">{helperText}</Typography> : null}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderAktivrettungTimeField = (candidateId, lane, result, fieldPath, label, actionLabel, stopAfterCapture = false, options = {}) => {
    const compact = options.compact === true;
    const timerKey = getTimerKeyForCandidate(candidateId, lane);
    const timerState = timerKey ? timerStates[timerKey] : null;
    const capturedValue = Number(getValueAtPath(result, fieldPath) || 0);
    const currentValue = timerState?.running && !capturedValue && (fieldPath === 'totalMs' || fieldPath.startsWith('sectionTimes.'))
      ? getRunningElapsedMs(timerKey, capturedValue)
      : capturedValue;
    const captureDisabled = !canEditFitnessSheet
      || !lane?.id
      || !lane.examinerUserId
      || !lane.controllerUserId
      || !timerState?.running;

    const content = (
      <Stack spacing={compact ? 1 : 1.25}>
        <Typography variant={compact ? 'body2' : 'subtitle2'} sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {formatDuration(currentValue)}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap={compact ? 'wrap' : undefined} useFlexGap={compact}>
          <Button size="small" variant="contained" onClick={() => handleCaptureAktivrettungTime(candidateId, fieldPath, stopAfterCapture)} disabled={captureDisabled}>
            {actionLabel}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleResetAktivrettungTimeField(candidateId, fieldPath)} disabled={!canEditFitnessSheet}>
            Zurücksetzen
          </Button>
          <Button size="small" variant="outlined" onClick={() => openManualTimeDialog(candidateId, fieldPath, label)} disabled={!canEditFitnessSheet}>
            Manuell
          </Button>
        </Stack>
      </Stack>
    );

    if (compact) {
      return content;
    }

    return <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>{content}</Paper>;
  };

  const summaryRows = candidates.map((candidate) => {
    const lane = getLaneForCandidate(candidate.id);
    const result = getResultForCandidate(candidate.id);
    const effectiveTotalMs = getEffectiveFitnessCoordinationTotalMs(result);
    return {
      candidate,
      lane,
      result,
      examiner: examinerById.get(String(result.examinerUserId || '')),
      controller: controllerById.get(String(result.controllerUserId || '')),
      effectiveTotalMs,
      grade: getFitnessCoordinationGrade(effectiveTotalMs),
      statusChip: getSimpleSheetStatusChipProps(result),
      controlChip: getControlStatusChipProps(result),
    };
  });

  const activeGrade = getFitnessCoordinationGrade(getEffectiveFitnessCoordinationTotalMs(activeResult));
  const activeFitnessFailed = isFitnessCoordinationFailed(activeResult);
  const activeEffectiveTimeSource = getFitnessCoordinationEffectiveTimeSource(activeResult);
  const activeUsesAutomaticLowerTime = Boolean(
    Number(activeResult.totalMs || 0)
    && Number(activeResult.controllerReferenceMs || 0)
    && !isFitnessCoordinationControlWithinTolerance(activeResult),
  );
  const activeTimerRunning = Boolean(activeTimerKey && timerStates[activeTimerKey]?.running);
  const activeRunningDisplayMs = activeTimerKey
    ? getFitnessCoordinationDisplayMs(
      activeResult,
      getRunningElapsedMs(activeTimerKey, Math.max(Number(activeResult.secondIntermediateMs || 0), Number(activeResult.firstIntermediateMs || 0), 0)),
    )
    : 0;

  const aktivrettungSummaryRows = candidates.map((candidate) => {
    const lane = getLaneForCandidate(candidate.id);
    const result = {
      ...buildEmptyAktivrettungResult(),
      ...(resultsByCandidateId[String(candidate.id)] || {}),
    };
    const sectionSum = getAktivrettungSectionSum(result);
    const overallPoints = getAktivrettungOverallPoints(result);
    const grade = getAktivrettungGrade(overallPoints);
    const timePoints = getAktivrettungTimePoints(result.totalMs);
    const running = Boolean(timerStates[getTimerKeyForCandidate(candidate.id, lane)]?.running);

    return {
      candidate,
      lane,
      result,
      examiner: examinerById.get(String(result.examinerUserId || '')),
      controller: controllerById.get(String(result.controllerUserId || '')),
      sectionSum,
      timePoints,
      overallPoints,
      grade,
      failed: isAktivrettungFailed({ ...result, totalPoints: overallPoints }),
      statusChip: getScoredTimedStatusChipProps(result, running),
    };
  });

  const einzelueberpruefungSummaryRows = candidates.map((candidate) => {
    const lane = getLaneForCandidate(candidate.id);
    const result = {
      ...buildEmptyEinzelueberpruefungResult(),
      ...(resultsByCandidateId[String(candidate.id)] || {}),
    };
    const sectionSum = getEinzelueberpruefungSectionSum(result);
    const overallPoints = getEinzelueberpruefungOverallPoints(result);
    const grade = getEinzelueberpruefungGrade(overallPoints);
    const timePoints = getEinzelueberpruefungTimePoints(result.totalMs);
    const running = Boolean(timerStates[getTimerKeyForCandidate(candidate.id, lane)]?.running);

    return {
      candidate,
      lane,
      result,
      examiner: examinerById.get(String(result.examinerUserId || '')),
      controller: controllerById.get(String(result.controllerUserId || '')),
      sectionSum,
      timePoints,
      overallPoints,
      grade,
      failed: isEinzelueberpruefungFailed(result),
      statusChip: getScoredTimedStatusChipProps(result, running),
    };
  });

  const activeAktivrettungSectionSum = getAktivrettungSectionSum(activeResult);
  const activeAktivrettungTimePoints = getAktivrettungTimePoints(activeResult.totalMs);
  const activeAktivrettungOverallPoints = getAktivrettungOverallPoints(activeResult);
  const activeAktivrettungGrade = getAktivrettungGrade(activeAktivrettungOverallPoints);
  const activeAktivrettungFailed = isAktivrettungFailed(activeResult);
  const activeAktivrettungRunningDisplayMs = activeLane?.id
    ? getRunningElapsedMs(
      activeLane.id,
      Math.max(Number(activeResult.totalMs || 0), ...AKTIVRETTUNG_TIMEPOINT_SECTION_IDS.map((sectionId) => Number(activeResult.sectionTimes?.[sectionId] || 0)), 0),
    )
    : Number(activeResult.totalMs || 0);

  const activeEinzelueberpruefungSectionSum = getEinzelueberpruefungSectionSum(activeResult);
  const activeEinzelueberpruefungTimePoints = getEinzelueberpruefungTimePoints(activeResult.totalMs);
  const activeEinzelueberpruefungOverallPoints = getEinzelueberpruefungOverallPoints(activeResult);
  const activeEinzelueberpruefungGrade = getEinzelueberpruefungGrade(activeEinzelueberpruefungOverallPoints);
  const activeEinzelueberpruefungFailed = isEinzelueberpruefungFailed(activeResult);
  const activeEinzelueberpruefungRunningDisplayMs = activeLane?.id
    ? getRunningElapsedMs(activeLane.id, Number(activeResult.totalMs || 0))
    : Number(activeResult.totalMs || 0);

  const renderAktivrettungTotalTimeCard = (placement = 'top') => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TimerIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {placement === 'bottom' ? 'Gesamtzeit Abschluss' : 'Gesamtzeit'}
          </Typography>
          {activeTimerRunning ? <Chip label="Läuft" color="warning" size="small" /> : null}
        </Stack>
        <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {formatDuration(activeAktivrettungRunningDisplayMs)}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={() => handleStartAktivrettungTimer(activeCandidate.id)} disabled={!canEditFitnessSheet || !activeResult.examinerUserId || !activeResult.controllerUserId || activeTimerRunning || Number(activeResult.totalMs || 0) > 0}>Start</Button>
          <Button variant="outlined" onClick={() => handleCaptureAktivrettungTime(activeCandidate.id, 'totalMs', true)} disabled={!canEditFitnessSheet || !activeTimerRunning}>Stopp</Button>
          <Button variant="outlined" onClick={() => openManualTimeDialog(activeCandidate.id, 'totalMs', placement === 'bottom' ? 'Gesamtzeit Abschluss' : 'Gesamtzeit')} disabled={!canEditFitnessSheet}>Manuell eingeben</Button>
          <Button variant="outlined" onClick={() => handleResetAktivrettungTimes(activeCandidate.id)} disabled={!canEditFitnessSheet}>Zeiten zurücksetzen</Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`P Zeit: ${activeAktivrettungTimePoints}`} variant="outlined" />
          <Chip label={`Σ 1–8: ${activeAktivrettungSectionSum}`} variant="outlined" />
          <Chip label={`Gesamt: ${activeAktivrettungOverallPoints}`} color="primary" variant="outlined" />
        </Stack>
      </Stack>
    </Paper>
  );

  const renderEinzelueberpruefungTotalTimeCard = () => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TimerIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Gesamtzeit</Typography>
          {activeTimerRunning ? <Chip label="Läuft" color="warning" size="small" /> : null}
        </Stack>
        <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
          {formatDuration(activeEinzelueberpruefungRunningDisplayMs)}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={() => handleStartAktivrettungTimer(activeCandidate.id)} disabled={!canEditFitnessSheet || !activeResult.examinerUserId || !activeResult.controllerUserId || activeTimerRunning || Number(activeResult.totalMs || 0) > 0}>Start</Button>
          <Button variant="outlined" onClick={() => handleCaptureAktivrettungTime(activeCandidate.id, 'totalMs', true)} disabled={!canEditFitnessSheet || !activeTimerRunning}>Stopp</Button>
          <Button variant="outlined" onClick={() => openManualTimeDialog(activeCandidate.id, 'totalMs', 'Gesamtzeit')} disabled={!canEditFitnessSheet}>Manuell eingeben</Button>
          <Button variant="outlined" onClick={() => handleResetAktivrettungTimes(activeCandidate.id)} disabled={!canEditFitnessSheet}>Zeit zurücksetzen</Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`P Zeit: ${activeEinzelueberpruefungTimePoints}`} variant="outlined" />
          <Chip label={`Σ 1–11: ${activeEinzelueberpruefungSectionSum}`} variant="outlined" />
        </Stack>
      </Stack>
    </Paper>
  );

  const handleSaveActiveSheet = async () => {
    if (!onSave || !canEditFitnessSheet || actionBusy) {
      return;
    }

    try {
      await onSave(activeStationId, sanitizeStationForPersistence(activeStation));
    } catch {
      // The parent already exposes the actionable error message.
    }
  };

  const runFinalizeActiveSheet = async () => {
    try {
      setSheetValidationNotice(null);
      await onFinalize(activeStationId, sanitizeStationForPersistence(activeStation), activeCandidate.id);
    } catch {
      // The parent already exposes the actionable error message.
    }
  };

  const handleFinalizeActiveSheet = async () => {
    if (!onFinalize || !canEdit || isOffline || actionBusy || isActiveCandidateFinalized || !activeCandidate?.id) {
      return;
    }

    if (activeResult.safetyIncident?.answered !== true) {
      setSheetValidationNotice({
        severity: 'warning',
        message: 'Vor dem Finalisieren muss zuerst die Frage nach einem sicherheitsrelevanten Fehler mit Ja oder Nein beantwortet werden.',
      });
      return;
    }

    if (activeResult.safetyIncident?.hasIncident && !String(activeResult.safetyIncident?.description || '').trim()) {
      setSheetValidationNotice({
        severity: 'warning',
        message: 'Ein sicherheitsrelevanter Fehler muss vor dem Finalisieren beschrieben werden.',
      });
      return;
    }

    if (activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION && !Number(activeResult.controllerReferenceMs || 0)) {
      setSheetValidationNotice({
        severity: 'warning',
        message: 'Vor dem Finalisieren muss zuerst die Kontrollzeit für die Gesamtzeit eingetragen werden.',
      });
      return;
    }

    if (activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION && missingFitnessIntermediateTimes.length > 0) {
      setSheetValidationNotice(null);
      setFinalizeMissingTimesDialogOpen(true);
      return;
    }

    if (activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && !Number(activeResult.totalMs || 0)) {
      setSheetValidationNotice({
        severity: 'warning',
        message: 'Vor dem Finalisieren muss zuerst die Gesamtzeit eingetragen werden.',
      });
      return;
    }

    if (activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && missingAktivrettungTimeSections.length > 0) {
      setSheetValidationNotice(null);
      setFinalizeMissingTimesDialogOpen(true);
      return;
    }

    await runFinalizeActiveSheet();
  };

  const handleConfirmFinalizeWithMissingTimes = async () => {
    if (!onFinalize || !activeCandidate?.id || actionBusy) {
      return;
    }

    setFinalizeMissingTimesDialogOpen(false);
    await runFinalizeActiveSheet();
  };

  const handleConfirmUnlock = async () => {
    if (!onUnlock || !unlockDialog.reason.trim() || actionBusy || !activeCandidate?.id) {
      return;
    }

    try {
      await onUnlock(activeStationId, unlockDialog.reason.trim(), activeCandidate.id);
      setUnlockDialog({ open: false, reason: '' });
    } catch {
      // The parent already exposes the actionable error message.
    }
  };

  const renderSafetyIncidentCard = () => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Sicherheitsrelevanter Fehler</Typography>
        <Typography variant="body2" color="text.secondary">Liegt ein sicherheitsrelevanter Fehler vor?</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant={activeResult.safetyIncident?.answered === true && activeResult.safetyIncident?.hasIncident === true ? 'contained' : 'outlined'}
            color="error"
            onClick={handleOpenSafetyIncidentDialog}
            disabled={!canEditFitnessSheet}
          >
            Ja
          </Button>
          <Button
            variant={activeResult.safetyIncident?.answered === true && activeResult.safetyIncident?.hasIncident !== true ? 'contained' : 'outlined'}
            color="success"
            onClick={handleSaveSafetyIncidentNo}
            disabled={!canEditFitnessSheet}
          >
            Nein
          </Button>
        </Stack>
        {activeResult.safetyIncident?.answered !== true ? (
          <Alert severity="info">Vor dem Finalisieren dieses Teilnehmers muss diese Frage mit Ja oder Nein beantwortet werden.</Alert>
        ) : null}
        {activeResult.safetyIncident?.hasIncident ? (
          <Alert severity="error">
            {activeResult.safetyIncident?.description || 'Sicherheitsrelevanter Fehler ohne Beschreibung gespeichert.'}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );

  if (!examId) {
    return <Alert severity="info">Bitte zuerst eine Prüfung SRHT auswählen oder anlegen.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Grid container spacing={1.5}>
        {SRHT_EXAM_PRACTICAL_STATIONS.map((station) => {
          const selected = station.id === activeStationId;
          return (
            <Grid item xs={12} md={6} lg={4} key={station.id}>
              <Card
                variant="outlined"
                onClick={() => handleSelectStation(station.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelectStation(station.id);
                  }
                }}
                role="button"
                tabIndex={0}
                sx={{
                  height: '100%',
                  borderColor: selected ? 'primary.main' : 'divider',
                  boxShadow: selected ? 2 : 0,
                  cursor: 'pointer',
                }}
              >
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{station.title}</Typography>
                    </Stack>
                    {station.subtitle ? <Typography variant="body2" color="text.secondary">{station.subtitle}</Typography> : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {activeStationId !== SRHT_EXAM_STATION_FITNESS_COORDINATION && activeStationId !== SRHT_EXAM_STATION_AKTIVRETTUNG && activeStationId !== SRHT_EXAM_STATION_FALLSCHUTZ && activeStationId !== SRHT_EXAM_STATION_GRUPPENPRUEFUNG && activeStationId !== SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG ? (
        <Alert severity="info">Der Prüfungsbogen für diesen Prüfungsteil folgt im nächsten Schritt. Prüfbogen 1 ist bereits vollständig zur Zeitnahme vorbereitet.</Alert>
      ) : null}

      {practicalEditBlockMessage ? (
        <Alert severity={practicalEditBlockMessage.severity}>{practicalEditBlockMessage.message}</Alert>
      ) : null}

      {isGroupPracticalStation(activeStationId) ? (
        <SrhtExamGroupPracticalPanel
          stationCode={activeStationId}
          stationState={activeStation}
          candidates={candidates}
          examiners={examiners}
          sheetRecord={activeSheetRecord}
          isSheetFinalized={isActiveSheetFinalized}
          canEdit={!blockedByPermission && !blockedByExamLock}
          isOffline={isOffline}
          actionBusy={actionBusy}
          onChangeStation={replaceActiveStation}
          onSyncTimerSession={onSyncTimerSession}
          onSave={onSave}
          onFinalize={onFinalize}
          onUnlock={onUnlock}
        />
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  {isActiveSheetFinalized && activeSheetRecord?.finalizedAt ? <Chip label={`Station vollständig finalisiert ${new Date(activeSheetRecord.finalizedAt).toLocaleString('de-DE')}`} color="success" variant="outlined" /> : null}
                </Stack>
              </Stack>

              {isActiveCandidateFinalized ? <Alert severity="info">Dieser Teilnehmer ist in Prüfbogen 1 finalisiert und schreibgeschützt. Für weitere Änderungen ist eine Entsperrung mit Begründung erforderlich.</Alert> : null}

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Prüfbogen 1 - Fitness- & Koordinationsprüfung
                </Typography>
              </Box>

              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Teilnehmer</Typography>
                {candidates.length === 0 ? (
                  <Alert severity="info">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Alert>
                ) : (
                  <Grid container spacing={1.25}>
                    {summaryRows.map((row) => (
                      <Grid item xs={12} md={6} xl={4} key={row.candidate.id}>
                        <Card
                          variant="outlined"
                          onClick={() => handleSelectCandidate(row.candidate.id)}
                          sx={{
                            cursor: 'pointer',
                            height: '100%',
                            borderColor: String(row.candidate.id) === selectedCandidateId ? 'primary.main' : 'divider',
                            boxShadow: String(row.candidate.id) === selectedCandidateId ? 2 : 0,
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.candidate.fullName}</Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip {...row.statusChip} size="small" />
                                <Chip {...row.controlChip} size="small" variant="outlined" />
                                {row.grade ? <Chip label={`Note ${row.grade}`} size="small" color={getGradeChipColor(row.grade)} variant="outlined" /> : null}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ablauf</Typography>
                      {FITNESS_COORDINATION_SEQUENCE.map((item, index) => (
                        <Typography key={item} variant="body2" color="text.secondary">{`${index + 1}. ${item}`}</Typography>
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        Steighöhe: 13 Meter bis zur Unterkante der Konstruktion als Anschlagpunkt.
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notenschlüssel</Typography>
                      <Typography variant="body2" color="text.secondary">Sehr gut: &lt; 05:30 min</Typography>
                      <Typography variant="body2" color="text.secondary">Gut: 05:30 min bis 06:30 min</Typography>
                      <Typography variant="body2" color="text.secondary">Befriedigend: 06:31 min bis 09:00 min</Typography>
                      <Typography variant="body2" color="text.secondary">Ausreichend: 09:01 min bis 11:00 min</Typography>
                      <Typography variant="body2" color="error">Mangelhaft: 11:01 min bis 14:00 min</Typography>
                      <Typography variant="body2" color="error">Ungenügend: &gt; 14:00 min</Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ausrüstung</Typography>
                      {FITNESS_COORDINATION_EQUIPMENT.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary">- {item}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Grundsätzliche Hinweise</Typography>
                      {FITNESS_COORDINATION_NOTES.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary">- {item}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  {isActiveSheetFinalized && activeSheetRecord?.finalizedAt ? <Chip label={`Station vollständig finalisiert ${new Date(activeSheetRecord.finalizedAt).toLocaleString('de-DE')}`} color="success" variant="outlined" /> : null}
                </Stack>
              </Stack>

              {isActiveCandidateFinalized ? <Alert severity="info">Dieser Teilnehmer ist in Prüfbogen 2 finalisiert und schreibgeschützt. Für weitere Änderungen ist eine Entsperrung mit Begründung erforderlich.</Alert> : null}

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Prüfbogen 2 - Aktivrettung
                </Typography>
              </Box>

              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Teilnehmer</Typography>
                {candidates.length === 0 ? (
                  <Alert severity="info">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Alert>
                ) : (
                  <Grid container spacing={1.25}>
                    {aktivrettungSummaryRows.map((row) => (
                      <Grid item xs={12} md={6} xl={4} key={row.candidate.id}>
                        <Card
                          variant="outlined"
                          onClick={() => handleSelectCandidate(row.candidate.id)}
                          sx={{
                            cursor: 'pointer',
                            height: '100%',
                            borderColor: String(row.candidate.id) === selectedCandidateId ? 'primary.main' : 'divider',
                            boxShadow: String(row.candidate.id) === selectedCandidateId ? 2 : 0,
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.candidate.fullName}</Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip label={row.result.status === 'finalized' ? 'Finalisiert' : 'Offen'} size="small" color={row.result.status === 'finalized' ? 'success' : 'default'} />
                                {row.grade ? <Chip label={getAktivrettungGradeLabel(row.grade)} size="small" color={getGradeChipColor(row.grade, row.failed)} variant="outlined" /> : null}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={7}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aufgabenbeschreibung</Typography>
                      <Typography variant="body2" color="text.secondary">{AKTIVRETTUNG_DESCRIPTION}</Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zur Verfügung gestelltes Material</Typography>
                      {AKTIVRETTUNG_MATERIAL.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary">• {item}</Typography>
                      ))}
                      <Typography variant="body2" color="text.secondary">Das Material kann nach dem Verlesen der Aufgabenstellung und vor Beginn der Zeitnahme vorbereitet werden.</Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bewertungszeit</Typography>
                      {AKTIVRETTUNG_TIME_POINT_RULES.map((rule) => (
                        <Typography key={rule.label} variant="body2" color="text.secondary">{`${rule.label} = ${rule.points} Punkt${rule.points === 1 ? '' : 'e'}`}</Typography>
                      ))}
                      <Typography variant="body2" color="error">Über 30 Minuten = nicht bestanden</Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notenschlüssel</Typography>
                      {AKTIVRETTUNG_GRADE_RANGES.map((range) => (
                        <Typography
                          key={range.grade}
                          variant="body2"
                          color={range.grade >= 5 ? 'error' : 'text.secondary'}
                        >
                          {formatAktivrettungGradeRangeLabel(range)}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  {isActiveSheetFinalized && activeSheetRecord?.finalizedAt ? <Chip label={`Station vollständig finalisiert ${new Date(activeSheetRecord.finalizedAt).toLocaleString('de-DE')}`} color="success" variant="outlined" /> : null}
                </Stack>
              </Stack>

              {isActiveCandidateFinalized ? <Alert severity="info">Dieser Teilnehmer ist in Prüfbogen 5 finalisiert und schreibgeschützt. Für weitere Änderungen ist eine Entsperrung mit Begründung erforderlich.</Alert> : null}

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Prüfbogen 5 - Einzelüberprüfung
                </Typography>
              </Box>

              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Teilnehmer</Typography>
                {candidates.length === 0 ? (
                  <Alert severity="info">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Alert>
                ) : (
                  <Grid container spacing={1.25}>
                    {einzelueberpruefungSummaryRows.map((row) => (
                      <Grid item xs={12} md={6} xl={4} key={row.candidate.id}>
                        <Card
                          variant="outlined"
                          onClick={() => handleSelectCandidate(row.candidate.id)}
                          sx={{
                            cursor: 'pointer',
                            height: '100%',
                            borderColor: String(row.candidate.id) === selectedCandidateId ? 'primary.main' : 'divider',
                            boxShadow: String(row.candidate.id) === selectedCandidateId ? 2 : 0,
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.candidate.fullName}</Typography>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                <Chip label={row.result.status === 'finalized' ? 'Finalisiert' : 'Offen'} size="small" color={row.result.status === 'finalized' ? 'success' : 'default'} />
                                {row.grade ? <Chip label={getEinzelueberpruefungGradeLabel(row.grade)} size="small" color={getGradeChipColor(row.grade, row.failed)} variant="outlined" /> : null}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={7}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1.25}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Aufgabenbeschreibung</Typography>
                      {EINZELUEBERPRUEFUNG_DESCRIPTION.map((paragraph) => (
                        <Typography key={paragraph} variant="body2" color="text.secondary">{paragraph}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Zur Verfügung gestelltes Material</Typography>
                      {EINZELUEBERPRUEFUNG_MATERIAL.map((item) => (
                        <Typography key={item} variant="body2" color="text.secondary">• {item}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bewertungszeit</Typography>
                      {EINZELUEBERPRUEFUNG_TIME_POINT_RULES.map((rule) => (
                        <Typography key={rule.label} variant="body2" color="text.secondary">{`${rule.label} = ${rule.points} Punkt${rule.points === 1 ? '' : 'e'}`}</Typography>
                      ))}
                      <Typography variant="body2" color="error">&gt; 35 min = 0 Punkte & nicht bestanden</Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notenschlüssel</Typography>
                      {EINZELUEBERPRUEFUNG_GRADE_RANGES.map((range) => (
                        <Typography key={range.grade} variant="body2" color={range.grade >= 5 ? 'error' : 'text.secondary'}>{`${range.label}: ${range.max}–${range.min}`}</Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION && examiners.length === 0 ? (
        <Alert severity="warning">Für die Zeitnahme müssen im Reiter Verwaltung zunächst Prüfer für die Prüfung hinterlegt werden.</Alert>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION && (
        activeCandidate ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{activeCandidate.fullName}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Chip {...getSimpleSheetStatusChipProps(activeResult)} />
                    <Chip label={activeFitnessFailed ? 'Nicht bestanden' : 'In Wertung'} color={activeFitnessFailed ? 'error' : 'success'} />
                    <Chip label={getFitnessCoordinationGradeLabel(activeGrade)} color={activeFitnessFailed ? 'error' : getGradeChipColor(activeGrade)} />
                  </Stack>
                </Stack>

                {renderExaminerAssignmentCard(activeCandidate.id, {
                  examinerLabel: 'Zeitnehmer / Prüfer',
                  controllerLabel: 'Kontrolleur / 2. Zeitnehmer',
                  helperText: 'Wird vor dem Start der Zeitnahme festgelegt. Kontrolleur darf nicht dieselbe Person wie der Zeitnehmer sein.',
                })}

                {sheetValidationNotice ? <Alert severity={sheetValidationNotice.severity}>{sheetValidationNotice.message}</Alert> : null}

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimerIcon fontSize="small" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Laufende Zeit</Typography>
                      {activeTimerRunning ? <Chip label="Läuft" color="warning" size="small" /> : null}
                    </Stack>
                    <Typography variant="h4" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                      {activeLane?.id ? formatDuration(activeRunningDisplayMs) : formatDuration(Math.max(Number(activeResult.totalMs || 0), Number(activeResult.secondIntermediateMs || 0), Number(activeResult.firstIntermediateMs || 0), 0))}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="contained" onClick={() => handleStartTimer(activeCandidate.id)} disabled={!canEditFitnessSheet || !activeResult.examinerUserId || !activeResult.controllerUserId || activeTimerRunning || Number(activeResult.totalMs || 0) > 0}>Start</Button>
                      <Button variant="outlined" onClick={() => handleCaptureTime(activeCandidate.id, 'totalMs', true)} disabled={!canEditFitnessSheet || !activeTimerRunning}>Gesamtzeit stoppen</Button>
                      <Button variant="outlined" onClick={() => handleResetCandidateTimes(activeCandidate.id)} disabled={!canEditFitnessSheet}>Zeiten zurücksetzen</Button>
                    </Stack>
                  </Stack>
                </Paper>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    {renderCheckpointCard(activeCandidate.id, activeLane, activeResult, 'firstIntermediateMs', '1. Zwischenzeit nach 1. Anschlag', 'Zwischenzeit setzen')}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {renderCheckpointCard(activeCandidate.id, activeLane, activeResult, 'secondIntermediateMs', '2. Zwischenzeit nach 2. Anschlag', 'Zwischenzeit setzen')}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {renderCheckpointCard(activeCandidate.id, activeLane, activeResult, 'totalMs', 'Stopp bei freien Seilstrecken', 'Stopp', true)}
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Maßgebliche Gesamtzeit</Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
                      <Button
                        variant={activeEffectiveTimeSource === FITNESS_COORDINATION_TIME_SOURCE_MEASURED ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => handleSelectEffectiveTimeSource(activeCandidate.id, FITNESS_COORDINATION_TIME_SOURCE_MEASURED)}
                        disabled={!canEditFitnessSheet || !activeResult.totalMs || !activeResult.controllerReferenceMs || activeUsesAutomaticLowerTime}
                      >
                        {`App-Zeit ${formatDuration(activeResult.totalMs || 0)}`}
                      </Button>
                      <Button
                        variant={activeEffectiveTimeSource === FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER ? 'contained' : 'outlined'}
                        color="secondary"
                        onClick={() => handleSelectEffectiveTimeSource(activeCandidate.id, FITNESS_COORDINATION_TIME_SOURCE_CONTROLLER)}
                        disabled={!canEditFitnessSheet || !activeResult.totalMs || !activeResult.controllerReferenceMs || activeUsesAutomaticLowerTime}
                      >
                        {activeResult.controllerReferenceMs ? `Kontrollzeit ${formatDuration(activeResult.controllerReferenceMs)}` : 'Kontrollzeit fehlt'}
                      </Button>
                      <Chip label={`Maßgeblich ${formatDuration(getEffectiveFitnessCoordinationTotalMs(activeResult) || activeResult.totalMs || 0)}`} color="primary" variant="outlined" />
                      <Button variant="outlined" onClick={() => openManualTimeDialog(activeCandidate.id, 'controllerReferenceMs', 'Kontrollzeit Gesamtzeit')} disabled={!canEditFitnessSheet || !activeResult.totalMs || !activeResult.controllerUserId}>Kontrollzeit eingeben</Button>
                    </Stack>
                    {activeResult.controllerReferenceMs && activeUsesAutomaticLowerTime ? <Alert severity="warning">Die Abweichung ist größer als 5 Sekunden. Für die Bewertung wird automatisch die niedrigere Zeit genutzt.</Alert> : null}
                  </Stack>
                </Paper>

                <TextField label="Bemerkungen" value={activeResult.remarks || ''} onChange={(event) => updateCandidateResult(activeCandidate.id, { remarks: event.target.value })} multiline minRows={2} fullWidth disabled={!canEditFitnessSheet} sx={getReadableDisabledFieldSx()} />

                {renderSafetyIncidentCard()}

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                      {isActiveCandidateFinalized ? <Chip label="Finalisiert" color="success" size="small" /> : null}
                    </Stack>
                    {isActiveCandidateFinalized ? (
                      <>
                        <TextField
                          label="Begründung zum Entsperren"
                          value={unlockDialog.reason}
                          onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
                          placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                          required
                          error={!String(unlockDialog.reason || '').trim()}
                          helperText={!String(unlockDialog.reason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                        />
                        <Button variant="outlined" color="warning" onClick={handleConfirmUnlock} disabled={!unlockDialog.reason.trim() || Boolean(actionBusy)}>
                          {actionBusy === 'unlock' ? 'Entsperrt...' : 'Bewerber entsperren'}
                        </Button>
                      </>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button variant="contained" onClick={handleSaveActiveSheet} disabled={!canEditFitnessSheet || Boolean(actionBusy)} fullWidth>
                          {actionBusy === 'save' ? 'Speichert...' : 'Speichern'}
                        </Button>
                        <Button variant="contained" color="success" onClick={handleFinalizeActiveSheet} disabled={!canEdit || isOffline || Boolean(actionBusy) || isActiveCandidateFinalized || !activeCandidate?.id} fullWidth>
                          {actionBusy === 'finalize' ? 'Finalisiert...' : 'Finalisieren'}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        ) : <Alert severity="info">Bitte wählen Sie einen Teilnehmer aus.</Alert>
      )}

      {activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Erfasste Ergebnisse</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Zeitnehmer</TableCell>
                      <TableCell>Kontrolleur</TableCell>
                      <TableCell>1. Zwz. nach 1. Anschlag</TableCell>
                      <TableCell>2. Zwz. nach 2. Anschlag</TableCell>
                      <TableCell>Maßgebliche Zeit</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryRows.map((row) => (
                      <TableRow key={row.candidate.id}>
                        <TableCell>{row.candidate.fullName}</TableCell>
                        <TableCell>{row.examiner ? getSelectableUserName(row.examiner) : ''}</TableCell>
                        <TableCell>{row.controller ? getSelectableUserName(row.controller) : ''}</TableCell>
                        <TableCell>{row.result.firstIntermediateMs ? formatDuration(row.result.firstIntermediateMs) : ''}</TableCell>
                        <TableCell>{row.result.secondIntermediateMs ? formatDuration(row.result.secondIntermediateMs) : ''}</TableCell>
                        <TableCell>
                          {row.effectiveTotalMs ? (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <span>{formatDuration(row.effectiveTotalMs)}</span>
                              <Chip size="small" {...row.controlChip} />
                            </Stack>
                          ) : ''}
                        </TableCell>
                        <TableCell>
                          {row.grade ? (
                            <Chip label={`Note ${row.grade}`} color={getGradeChipColor(row.grade, row.failed)} size="small" variant="outlined" />
                          ) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    {summaryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && examiners.length === 0 ? (
        <Alert severity="warning">Für die Aktivrettung müssen im Reiter Verwaltung zunächst Prüfer für die Prüfung hinterlegt werden.</Alert>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG && examiners.length === 0 ? (
        <Alert severity="warning">Für die Einzelüberprüfung müssen im Reiter Verwaltung zunächst Prüfer für die Prüfung hinterlegt werden.</Alert>
      ) : null}

      {activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && (
        activeCandidate ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{activeCandidate.fullName}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Chip label={activeAktivrettungFailed ? 'Nicht bestanden' : 'In Wertung'} color={activeAktivrettungFailed ? 'error' : 'success'} />
                    <Chip label={getAktivrettungGradeLabel(activeAktivrettungGrade)} color={activeAktivrettungFailed ? 'error' : 'primary'} />
                  </Stack>
                </Stack>

                {renderExaminerAssignmentCard(activeCandidate.id, {
                  examinerLabel: 'Prüfer',
                  controllerLabel: 'Kontrolleur',
                  helperText: 'Ein Kontrolleur darf mehrere Prüfer zeitgleich kontrollieren.',
                })}

                {sheetValidationNotice ? <Alert severity={sheetValidationNotice.severity}>{sheetValidationNotice.message}</Alert> : null}

                {renderAktivrettungTotalTimeCard('top')}

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bewertung der 8 Teilabschnitte</Typography>
                  {AKTIVRETTUNG_SECTION_DEFINITIONS.map((section) => {
                    const selectedValue = String(activeResult.sectionScores?.[section.id] ?? '');
                    return (
                      <Paper key={section.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.25}>
                          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{`${section.number}. ${section.title}`}</Typography>
                            <Chip label={selectedValue ? `${selectedValue} Punkte` : 'Noch nicht bewertet'} size="small" color={selectedValue ? 'primary' : 'default'} />
                          </Stack>
                          {section.capturesTimestamp ? (
                            <Box sx={{ p: 1.25, borderRadius: 1.5, border: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: 'action.hover' }}>
                              {renderAktivrettungTimeField(
                                activeCandidate.id,
                                activeLane,
                                activeResult,
                                `sectionTimes.${section.id}`,
                                `Zwischenzeit nach Abschnitt ${section.number}`,
                                'Zwischenzeit setzen',
                                false,
                                { compact: true },
                              )}
                            </Box>
                          ) : null}
                          <Stack spacing={0.25}>
                            {section.items.map((item) => (
                              <Typography key={item} variant="body2" color="text.secondary">• {item}</Typography>
                            ))}
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {section.scoreOptions.map((option) => {
                              const selected = selectedValue === String(option.points);
                              return (
                                <Button
                                  key={`${section.id}-${option.points}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'outlined'}
                                  color={option.points <= 1 ? 'error' : option.points === 2 ? 'warning' : 'primary'}
                                  onClick={() => handleChangeAktivrettungSectionScore(activeCandidate.id, section.id, option.points)}
                                  disabled={!canEditFitnessSheet}
                                >
                                  {`${option.points} P · ${option.label}`}
                                </Button>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>

                {renderAktivrettungTotalTimeCard('bottom')}

                <TextField label="Bemerkungen" value={activeResult.remarks || ''} onChange={(event) => updateCandidateResult(activeCandidate.id, { remarks: event.target.value })} multiline minRows={3} fullWidth disabled={!canEditFitnessSheet} sx={getReadableDisabledFieldSx()} />

                {renderSafetyIncidentCard()}

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                      {isActiveCandidateFinalized ? <Chip label="Finalisiert" color="success" size="small" /> : null}
                    </Stack>
                    {isActiveCandidateFinalized ? (
                      <>
                        <TextField
                          label="Begründung zum Entsperren"
                          value={unlockDialog.reason}
                          onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
                          placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                          required
                          error={!String(unlockDialog.reason || '').trim()}
                          helperText={!String(unlockDialog.reason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                        />
                        <Button variant="outlined" color="warning" onClick={handleConfirmUnlock} disabled={!unlockDialog.reason.trim() || Boolean(actionBusy)}>
                          {actionBusy === 'unlock' ? 'Entsperrt...' : 'Teilnehmer entsperren'}
                        </Button>
                      </>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button variant="contained" onClick={handleSaveActiveSheet} disabled={!canEditFitnessSheet || Boolean(actionBusy)} fullWidth>
                          {actionBusy === 'save' ? 'Speichert...' : 'Speichern'}
                        </Button>
                        <Button variant="contained" color="success" onClick={handleFinalizeActiveSheet} disabled={!canEdit || isOffline || Boolean(actionBusy) || isActiveCandidateFinalized || !activeCandidate?.id} fullWidth>
                          {actionBusy === 'finalize' ? 'Finalisiert...' : 'Finalisieren'}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        ) : <Alert severity="info">Bitte wählen Sie einen Teilnehmer aus.</Alert>
      )}

      {activeStationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG && (
        activeCandidate ? (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{activeCandidate.fullName}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Chip label={activeEinzelueberpruefungFailed ? 'Nicht bestanden' : 'In Wertung'} color={activeEinzelueberpruefungFailed ? 'error' : 'success'} />
                    <Chip label={getEinzelueberpruefungGradeLabel(activeEinzelueberpruefungGrade)} color={activeEinzelueberpruefungFailed ? 'error' : 'primary'} />
                  </Stack>
                </Stack>

                {renderExaminerAssignmentCard(activeCandidate.id, {
                  examinerLabel: 'Prüfer',
                  controllerLabel: 'Kontrolleur',
                  helperText: 'Ein Kontrolleur darf mehrere Prüfer zeitgleich kontrollieren.',
                })}

                {renderEinzelueberpruefungTotalTimeCard()}

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bewertung der 11 Teilabschnitte</Typography>
                  {EINZELUEBERPRUEFUNG_SECTION_DEFINITIONS.map((section) => {
                    const selectedValue = String(activeResult.sectionScores?.[section.id] ?? '');
                    return (
                      <Paper key={section.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.25}>
                          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{`${section.number}. ${section.title}`}</Typography>
                            <Chip label={selectedValue ? `${selectedValue} Punkte` : 'Noch nicht bewertet'} size="small" color={selectedValue ? 'primary' : 'default'} />
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {section.scoreOptions.map((option) => {
                              const selected = selectedValue === String(option.points);
                              return (
                                <Button
                                  key={`${section.id}-${option.points}`}
                                  size="small"
                                  variant={selected ? 'contained' : 'outlined'}
                                  color={option.points === 0 ? 'error' : option.points <= 6 ? 'warning' : 'primary'}
                                  onClick={() => handleChangeAktivrettungSectionScore(activeCandidate.id, section.id, option.points)}
                                  disabled={!canEditFitnessSheet}
                                >
                                  {`${option.points} P · ${option.label}`}
                                </Button>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>

                {renderEinzelueberpruefungTotalTimeCard()}

                <TextField label="Bemerkungen" value={activeResult.remarks || ''} onChange={(event) => updateCandidateResult(activeCandidate.id, { remarks: event.target.value })} multiline minRows={3} fullWidth disabled={!canEditFitnessSheet} sx={getReadableDisabledFieldSx()} />

                {renderSafetyIncidentCard()}

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.25}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Bearbeitungsschutz</Typography>
                      {isActiveCandidateFinalized ? <Chip label="Finalisiert" color="success" size="small" /> : null}
                    </Stack>
                    {isActiveCandidateFinalized ? (
                      <>
                        <TextField
                          label="Begründung zum Entsperren"
                          value={unlockDialog.reason}
                          onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
                          placeholder="z. B. Zahlendreher oder fehlende Ergänzung"
                          required
                          error={!String(unlockDialog.reason || '').trim()}
                          helperText={!String(unlockDialog.reason || '').trim() ? 'Bitte eine Begründung als Freitext eingeben.' : ' '}
                        />
                        <Button variant="outlined" color="warning" onClick={handleConfirmUnlock} disabled={!unlockDialog.reason.trim() || Boolean(actionBusy)}>
                          {actionBusy === 'unlock' ? 'Entsperrt...' : 'Teilnehmer entsperren'}
                        </Button>
                      </>
                    ) : (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button variant="contained" onClick={handleSaveActiveSheet} disabled={!canEditFitnessSheet || Boolean(actionBusy)} fullWidth>
                          {actionBusy === 'save' ? 'Speichert...' : 'Speichern'}
                        </Button>
                        <Button variant="contained" color="success" onClick={handleFinalizeActiveSheet} disabled={!canEdit || isOffline || Boolean(actionBusy) || isActiveCandidateFinalized || !activeCandidate?.id} fullWidth>
                          {actionBusy === 'finalize' ? 'Finalisiert...' : 'Finalisieren'}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        ) : <Alert severity="info">Bitte wählen Sie einen Teilnehmer aus.</Alert>
      )}

      {activeStationId === SRHT_EXAM_STATION_AKTIVRETTUNG && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Erfasste Ergebnisse Aktivrettung</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Prüfer</TableCell>
                      <TableCell>Kontrolleur</TableCell>
                      <TableCell>P Zeit</TableCell>
                      <TableCell>Zeit</TableCell>
                      <TableCell>Σ 1–8</TableCell>
                      <TableCell>Gesamt</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aktivrettungSummaryRows.map((row) => (
                      <TableRow key={row.candidate.id}>
                        <TableCell>{row.candidate.fullName}</TableCell>
                        <TableCell>{row.examiner ? getSelectableUserName(row.examiner) : ''}</TableCell>
                        <TableCell>{row.controller ? getSelectableUserName(row.controller) : ''}</TableCell>
                        <TableCell>{row.timePoints}</TableCell>
                        <TableCell>{row.result.totalMs ? formatDuration(row.result.totalMs) : ''}</TableCell>
                        <TableCell>{row.sectionSum}</TableCell>
                        <TableCell>{row.overallPoints}</TableCell>
                        <TableCell>
                          {row.grade ? (
                            <Chip label={getAktivrettungGradeLabel(row.grade)} color={getGradeChipColor(row.grade, row.failed)} size="small" />
                          ) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    {aktivrettungSummaryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {activeStationId === SRHT_EXAM_STATION_EINZELUEBERPRUEFUNG && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Erfasste Ergebnisse Einzelüberprüfung</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Prüfer</TableCell>
                      <TableCell>Kontrolleur</TableCell>
                      <TableCell>P Zeit</TableCell>
                      <TableCell>Zeit</TableCell>
                      <TableCell>Σ 1–11</TableCell>
                      <TableCell>Gesamt</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {einzelueberpruefungSummaryRows.map((row) => (
                      <TableRow key={row.candidate.id}>
                        <TableCell>{row.candidate.fullName}</TableCell>
                        <TableCell>{row.examiner ? getSelectableUserName(row.examiner) : ''}</TableCell>
                        <TableCell>{row.controller ? getSelectableUserName(row.controller) : ''}</TableCell>
                        <TableCell>{row.timePoints}</TableCell>
                        <TableCell>{row.result.totalMs ? formatDuration(row.result.totalMs) : ''}</TableCell>
                        <TableCell>{row.sectionSum}</TableCell>
                        <TableCell>{row.overallPoints}</TableCell>
                        <TableCell>
                          {row.grade ? (
                            <Chip label={getEinzelueberpruefungGradeLabel(row.grade)} color={getGradeChipColor(row.grade, row.failed)} size="small" />
                          ) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    {einzelueberpruefungSummaryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary">Für diese Prüfung wurden noch keine Teilnehmer übernommen.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={examinerDialog.open} onClose={handleCloseExaminerDialog} fullWidth maxWidth="sm">
        <DialogTitle>Prüfer bearbeiten</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label={activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION ? 'Zeitnehmer / Prüfer' : 'Prüfer'}
              value={examinerDialog.examinerUserId}
              onChange={(event) => setExaminerDialog((current) => ({
                ...current,
                examinerUserId: String(event.target.value),
                controllerUserId: String(current.controllerUserId || '') === String(event.target.value || '') ? '' : current.controllerUserId,
              }))}
              sx={getReadableDisabledFieldSx()}
            >
              <MenuItem value="">Auswählen</MenuItem>
              {getAvailableExaminers(examinerDialog.candidateId).map((examiner) => {
                const examinerUserId = String(getSelectableUserId(examiner));
                return <MenuItem key={examinerUserId} value={examinerUserId}>{getSelectableUserName(examiner)}</MenuItem>;
              })}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label={activeStationId === SRHT_EXAM_STATION_FITNESS_COORDINATION ? 'Kontrolleur / 2. Zeitnehmer' : 'Kontrolleur'}
              value={examinerDialog.controllerUserId}
              onChange={(event) => setExaminerDialog((current) => ({ ...current, controllerUserId: String(event.target.value) }))}
              sx={getReadableDisabledFieldSx()}
            >
              <MenuItem value="">Auswählen</MenuItem>
              {getAvailableControllers(examinerDialog.candidateId, examinerDialog.examinerUserId).map((option) => {
                const optionUserId = String(getSelectableUserId(option));
                return <MenuItem key={optionUserId} value={optionUserId}>{getSelectableUserName(option)}</MenuItem>;
              })}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExaminerDialog}>Schließen</Button>
          <Button variant="contained" onClick={handleSaveExaminerDialog} disabled={!canEditFitnessSheet || Boolean(actionBusy)}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={safetyIncidentDialog.open} onClose={handleCloseSafetyIncidentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Sicherheitsrelevanter Fehler</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>Mit Speichern wird die Auswahl auf Ja gesetzt und der Fehler dokumentiert.</Alert>
          <TextField
            label="Beschreibung des sicherheitsrelevanten Fehlers"
            value={safetyIncidentDialog.description}
            onChange={(event) => setSafetyIncidentDialog((current) => ({ ...current, description: event.target.value }))}
            multiline
            minRows={4}
            fullWidth
            margin="normal"
            autoFocus
            sx={getReadableDisabledFieldSx()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSafetyIncidentDialog}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleSaveSafetyIncidentYes} disabled={!canEditFitnessSheet || Boolean(actionBusy) || !String(safetyIncidentDialog.description || '').trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualTimeDialog.open} onClose={closeManualTimeDialog} fullWidth maxWidth="xs">
        <DialogTitle>{manualTimeDialog.label || 'Zeit manuell eingeben'}</DialogTitle>
        <DialogContent>
          <TextField label="Zeit (MM:SS)" value={manualTimeDialog.value} onChange={(event) => setManualTimeDialog((current) => ({ ...current, value: event.target.value, error: '' }))} fullWidth margin="normal" error={Boolean(manualTimeDialog.error)} helperText={manualTimeDialog.error || 'Beispiel: 05:42'} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeManualTimeDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={confirmManualTimeDialog}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unlockDialog.open} onClose={() => setUnlockDialog({ open: false, reason: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Praktischen Prüfungsbogen entsperren</DialogTitle>
        <DialogContent>
          <TextField
            label="Begründung"
            value={unlockDialog.reason}
            onChange={(event) => setUnlockDialog((current) => ({ ...current, reason: event.target.value }))}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialog({ open: false, reason: '' })}>Abbrechen</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmUnlock} disabled={!unlockDialog.reason.trim() || Boolean(actionBusy)}>
            {actionBusy === 'unlock' ? 'Entsperrt...' : 'Entsperren'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={finalizeMissingTimesDialogOpen}
        onClose={() => setFinalizeMissingTimesDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{missingFinalizeTimesDialogConfig.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2">
              {missingFinalizeTimesDialogConfig.message}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Fehlende Zwischenzeiten: {missingFinalizeTimeLabels.join(', ')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinalizeMissingTimesDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="success" onClick={handleConfirmFinalizeWithMissingTimes} disabled={Boolean(actionBusy)}>
            Trotzdem finalisieren
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}